import { createActivationPollGate } from "../lib/residentActivationPoll";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { AlertCircle, Mic, Volume2, Phone, ZoomIn, ZoomOut, Contrast, Play } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import RealtimeChatScreen from "./RealtimeChatScreen";
import { useMagnification } from "../lib/useMagnification";
import ProfileHeader from "../components/kiosk/ProfileHeader";
import TodayPanel from "../components/kiosk/TodayPanel";
import RequestsPanel from "../components/kiosk/RequestsPanel";
import UpcomingPanel from "../components/kiosk/UpcomingPanel";
import RoomDevicePanel from "../components/kiosk/RoomDevicePanel";
import DemoKioskPanel from "../components/kiosk/demo/DemoKioskPanel";
import DemoSiteBar from "../components/kiosk/demo/DemoSiteBar";
import RoomScreenPreview from "../components/public/RoomScreenPreview";
import { sendRoomDeviceCommand } from "../lib/kioskDeviceControl";
import { createCallAudioCoordinator } from "../lib/callAudioDuck";
import { callAudioOptions } from "../lib/callAudioOptions";
import { logActivationClientEvent } from "../lib/activationClient";
import { useKioskMediaPrime, openInFullTab, inSandboxedIframe } from "../lib/useKioskMediaPrime";
import { useWakeWord } from "../lib/useWakeWord";
import { wakeUrlFromSearch } from "../lib/wakeWordClient";
import { retryKioskBootstrap } from "../lib/kioskBootstrapRetry";
import { useVoiceTransport } from "../lib/useVoiceTransport";

// Kiosk is PUBLIC - no login. Selected/identified by kiosk_id in URL.
// /kiosk/:kioskId  (use "demo" to pick an arbitrary kiosk automatically)
//
// 2026-08-09: retired the legacy turn-based (STT -> /api/ai/chat -> TTS)
// voice loop and its "Live"/"Turn" toggle. Full-duplex Realtime (WebRTC)
// via RealtimeChatScreen is now the only resident conversation path - see
// docs/ARIA_VOICE_FIRST.md for why (the two paths had independently
// drifted prompts, which was the actual root cause of a real user-facing
// bug). Medication reminders, which used to piggyback on the legacy
// speak() helper, now use the mode-independent announceLine() below so
// that feature has no gap from this change.

export default function Kiosk() {
  const { kioskId } = useParams();
  const nav = useNavigate();

  const [kiosk, setKiosk] = useState(null);
  const [resident, setResident] = useState(null);
  const [demoSetupError, setDemoSetupError] = useState(false); // /kiosk/demo with no public_demo kiosk configured
  const [callState, setCallState] = useState("idle"); // idle | calling | waiting | chatting
  const [alert, setAlert] = useState(null);
  const [devices, setDevices] = useState([]);
  const { micReady, primeMedia } = useKioskMediaPrime();
  const [needsTap, setNeedsTap] = useState(false);       // remote pendant fired but we need a user tap first (autoplay/mic policy)
  const [pendingAlert, setPendingAlert] = useState(null);
  // { id, pressCount } of the last open event this kiosk auto-woke for.
  // Versioned by press_count so a LATER pendant press that reactivates the
  // SAME open event (same alert_id, press_count bumped) still re-wakes Aria
  // - see kioskEmergencyWake.js / docs/LEVEL1_BREAKTEST.md (invariant 6).
  const seenEmergencyRef = useRef(null);
  const lastWakeDecisionRef = useRef(null);  // dedupe wake-decision breadcrumbs to transitions only
  const activationGateRef = useRef(createActivationPollGate());
  const triggerSourceRef = useRef("manual_kiosk");  // what's about to start the next RealtimeChatScreen — pendant | manual_kiosk | wake_word
  const callStateRef = useRef("idle");     // sync callState for async callbacks
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Accessibility — magnification (whole-screen, voice+touch-controlled,
  // see useMagnification.js) & high-contrast per kiosk, persisted so a room
  // set for a low-vision resident stays that way across reboots.
  const { scale: magnification, setScale: setMagnification } = useMagnification();
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem("caos_kiosk_hc") === "1");
  useEffect(() => { localStorage.setItem("caos_kiosk_hc", highContrast ? "1" : "0"); }, [highContrast]);

  // Voice preference (OpenAI TTS voice). Persisted per-kiosk so each room
  // can match its resident's preference.
  const VOICES = [
    { id: "marin",   label: "Marin",   desc: "Natural & warm" },
    { id: "cedar",   label: "Cedar",   desc: "Natural & grounded" },
    { id: "shimmer", label: "Shimmer", desc: "Soft & gentle" },
    { id: "coral",   label: "Coral",   desc: "Warm & inviting" },
    { id: "nova",    label: "Nova",    desc: "Bright & kind" },
    { id: "sage",    label: "Sage",    desc: "Articulate & refined" },
    { id: "fable",   label: "Fable",   desc: "Storytelling warmth" },
    { id: "ballad",  label: "Ballad",  desc: "Melodic & thoughtful" },
    { id: "alloy",   label: "Alloy",   desc: "Neutral & clear" },
    { id: "ash",     label: "Ash",     desc: "Deeper, calming" },
    { id: "onyx",    label: "Onyx",    desc: "Deep & reassuring" },
    { id: "echo",    label: "Echo",    desc: "Crisp & even" },
    { id: "verse",   label: "Verse",   desc: "Expressive & friendly" },
  ];
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem("caos_kiosk_voice") || "shimmer");
  const voiceMode = useVoiceTransport(kiosk?.kiosk_id);   // low-cost voice or Realtime, and the voice: kept per kiosk
  useEffect(() => { if (voiceMode.voice) setVoiceId(voiceMode.voice); }, [voiceMode.voice]);
  const voiceIdRef = useRef(voiceId);
  useEffect(() => {
    voiceIdRef.current = voiceId;
    localStorage.setItem("caos_kiosk_voice", voiceId);
  }, [voiceId]);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);

  // One serialized audio queue owns call duck + restore. A rapid new wake
  // cannot race an old call's restore and leave a one-way TV too loud.
  const callAudioRef = useRef(null);
  if (!callAudioRef.current) callAudioRef.current = createCallAudioCoordinator(sendRoomDeviceCommand, callAudioOptions());
  // Undo a duck an earlier page load never restored; restore on unmount (lib/callAudioDuck.js).
  useEffect(() => { const c = callAudioRef.current; c.recover(); return () => { c.end("kiosk_unmount"); }; }, []);

  // Load kiosk + resident
  useEffect(() => {
    let stopped = false;
    retryKioskBootstrap(async () => {
      let id = kioskId;
      if (id === "demo") {
          // 2026-09-01 (real live defect): this used to fetch every kiosk
          // and take kiosks[0] - database sort order, not a deliberate
          // choice, which is how a real test kiosk ("michael"/Room 121)
          // became the public face of the product. The ONLY thing the
          // public demo route may resolve to now is whichever kiosk an
          // admin has explicitly designated (Kiosks tab, "Public demo").
        const { data: demoKiosk } = await axios.get(`${API}/kiosks/public-demo`);
        id = demoKiosk.kiosk_id;
      }
      const { data } = await axios.get(`${API}/residents/public/by-kiosk/${id}`);
      if (stopped) return null;
      setKiosk(data.kiosk);
      setResident(data.resident);
      // Load smart devices for this room; this panel is nonfatal after the
      // identity bootstrap has succeeded.
      if (data.kiosk?.room) {
        try {
          const { data: allDev } = await axios.get(`${API}/devices/public/by-room/${data.kiosk.room}`);
          setDevices(allDev || []);
        } catch { /* keep the kiosk usable if devices are temporarily unavailable */ }
      }
      return true;
    }, { shouldStop: () => stopped }).catch((e) => {
      if (stopped) return;
      if (kioskId === "demo" && e?.response?.status === 404) {
        setDemoSetupError(true);   // the public preview below, never a dead end (#162)
      } else {
        toast.error("Could not load kiosk");
      }
    });
    return () => { stopped = true; };
  }, [kioskId]);

  // Keep the device panel current without a manual refresh - a voice-driven
  // change (Aria's tools) or a staff-side change both write the same
  // /devices state this reads, so a short poll is enough to reflect either
  // (see 2026-08-27 report, "same device state for screen and Aria").
  useEffect(() => {
    if (!kiosk?.room || callState !== "idle") return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/devices/public/by-room/${kiosk.room}`);
        if (!stop) setDevices(data || []);
      } catch { /* keep last known state on a transient network blip */ }
    };
    const t = setInterval(poll, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [kiosk?.room, callState]);

  // Kiosk presence breadcrumbs — so "when was the kiosk actually here" is
  // answerable from evidence (activation observability, 2026-09-07).
  useEffect(() => {
    if (!kiosk?.kiosk_id) return;
    const base = { kiosk_id: kiosk.kiosk_id, room: kiosk.room, resident_id: resident?.resident_id };
    logActivationClientEvent("kiosk_mounted", { ...base, data: { path: window.location.pathname } });
    const onVis = () => logActivationClientEvent(
      document.visibilityState === "hidden" ? "page_hidden" : "page_visible", base);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      logActivationClientEvent("kiosk_unmounted", base);
    };
  }, [kiosk?.kiosk_id, kiosk?.room, resident?.resident_id]);

  // Poll for incoming emergencies (panic-press / fall) → auto hands-free.
  // The accept/reject decision itself belongs to activationGateRef (keyed
  // on activation_id, with the connect-retry/backoff state that
  // realtimeConnection.js's recover() path drives via .finish() below) -
  // that gate is load-bearing for the concurrency-safe activation cycle
  // and must stay the single source of truth for "do we wake". The
  // observability breadcrumbs alongside it (alert_first_seen/cleared,
  // wake_accepted/rejected) are a separate, additive requirement (Level 1
  // observability, 2026-09-07) computed from the same poll result so
  // "when did the kiosk actually see/act on this" stays evidenced.
  useEffect(() => {
    if (!kiosk?.kiosk_id) return;
    let stop = false;
    const base = { kiosk_id: kiosk.kiosk_id, room: kiosk.room, resident_id: resident?.resident_id };
    logActivationClientEvent("poll_started", { ...base, data: { interval_ms: 3000 } });
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/kiosks/${kiosk.kiosk_id}/active-emergency`);
        if (stop) return;
        const a = data.alert;
        const prevId = seenEmergencyRef.current?.id || null;
        const crumb = a
          ? { ...base, alert_id: a.alert_id, activation_id: a.activation_id, data: { press_count: a.press_count } }
          : base;
        if (a && a.alert_id !== prevId) logActivationClientEvent("alert_first_seen", crumb);
        if (!a && prevId) logActivationClientEvent("alert_cleared", { ...base, alert_id: prevId });
        seenEmergencyRef.current = a ? { id: a.alert_id, pressCount: a.press_count || 0 } : null;

        const idle = callStateRef.current === "idle";
        const wake = activationGateRef.current.accept(a, idle);
        const reason = wake ? "accepted" : (!a ? "no_alert" : (!idle ? "rejected_in_call" : "rejected_gate"));
        // Log the wake decision only on a transition - never every idle 3s poll.
        const decisionKey = a ? `${a.alert_id}:${a.press_count}:${wake ? "wake" : reason}` : null;
        if (wake || (decisionKey && decisionKey !== lastWakeDecisionRef.current)) {
          logActivationClientEvent(wake ? "wake_accepted" : "wake_rejected",
            { ...crumb, data: { ...(crumb.data || {}), reason, call_state: callStateRef.current } });
        }
        lastWakeDecisionRef.current = decisionKey;
        if (wake) handleIncomingEmergency(a);
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => {
      stop = true; clearInterval(t);
      logActivationClientEvent("poll_stopped", base);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk]);

  // Watch for the current alert being resolved by staff → quietly end voice loop
  useEffect(() => {
    if (!alert?.alert_id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/alerts/public/${alert.alert_id}/status`);
        if (data.status === "resolved") {
          // The WebRTC loop owns hearing/speaking during a call - it owns the
          // goodbye too. Just close the call silently.
          setTimeout(() => cancelCall(), 500);
        }
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.alert_id]);

  const handleIncomingEmergency = async (a) => {
    triggerSourceRef.current = "pendant";
    setAlert(a);
    // If the browser hasn't received a user gesture yet, we cannot play audio
    // or open the mic. Defer until the user taps anywhere.
    if (!micReady) {
      setPendingAlert(a);
      setNeedsTap(true);
      setCallState("waiting");
      return;
    }
    await beginConversation(a);
  };

  const restoreAudioAfterCall = async (reason) => {
    await callAudioRef.current.end(reason);
  };

  const beginConversation = async (a) => {
    setCallState("chatting");
    // Turn room TVs/speakers down (never off) for the call; restored on every call end (lib/callAudioDuck.js).
    // Not awaited: 15 infrared presses take seconds and must not delay Aria's greeting (Room 214, 2026-10-12).
    callAudioRef.current.begin(devices, kiosk.room);
    // RealtimeChatScreen owns its own greeting + listening loop from here.
  };

  // Wake-endpoint trigger (EliteDesk home endpoint "Hey Aria", room-node/aria_wake,
  // enabled per endpoint with ?wake=1; the Voice PE will use the same protocol):
  // starts the same no-event conversation path as a manual talk,
  // just without a tap and without opening a resident event.
  // Public demo kiosk: typing starts the same no-event conversation, then the
  // call screen sends the text into it (realtimeTypedTurn.js).
  const isDemo = kiosk?.public_demo === true;
  // The public demo wears the public site's palette (kiosk-public, index.css);
  // real room screens keep theirs. High contrast overrides both.
  const a11yRootClass = [highContrast && "kiosk-hc", isDemo && "kiosk-public ws-root"].filter(Boolean).join(" ");
  const [initialTypedText, setInitialTypedText] = useState(null);
  const startTypedConversation = async (text) => {
    if (callStateRef.current !== "idle" || !(await primeMedia())) return false;
    triggerSourceRef.current = "manual_kiosk";
    setAlert(null);
    setInitialTypedText(text);
    beginConversation(null);
    return true;
  };

  const startWakeConversation = () => {
    if (callStateRef.current !== "idle") return;
    triggerSourceRef.current = "wake_word";
    setAlert(null);
    beginConversation(null);
  };
  useWakeWord({ url: wakeUrlFromSearch(window.location.search), callState, kiosk,
    residentId: resident?.resident_id, onWake: () => startWakeConversation() });

  // Non-conversational, mode-independent single-line TTS announcement.
  // Used for medication reminders - NOT part of any conversation, so it
  // doesn't need (and shouldn't use) the full-duplex Realtime peer
  // connection; a plain TTS clip is the right tool for "say this one thing."
  // Only ever called while callState is idle (see checkMeds below), so there
  // is no active RealtimeChatScreen session to conflict with.
  const announceLine = (text) =>
    new Promise((resolve) => {
      (async () => {
        try {
          const { data } = await axios.post(`${API}/ai/tts`, { text, voice: voiceIdRef.current }, { timeout: 8000 });
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audio.onended = () => resolve();
          audio.onerror = () => resolve();
          await audio.play();
        } catch {
          resolve();
        }
      })();
    });

  // Medication reminder polling — only when idle
  useEffect(() => {
    if (!kiosk?.room) return;
    let stop = false;
    const checkMeds = async () => {
      if (callStateRef.current !== "idle") return;
      try {
        const { data: due } = await axios.get(`${API}/medications/due/by-room/${kiosk.room}`);
        if (stop || !due || due.length === 0) return;
        const m = due[0];
        const name = (resident?.preferred_name || resident?.name || "there").split(" ")[0];
        const line = `Hi ${name}, it's time for your ${m.title}.${m.dose_notes ? " " + m.dose_notes + "." : ""} If you need help, press the red button.`;
        await announceLine(line);
        await axios.post(`${API}/medications/ack/${m.reminder_id}`).catch(() => {});
        toast.success(`Reminder spoken: ${m.title}`);
      } catch { /* silent */ }
    };
    checkMeds();
    const t = setInterval(checkMeds, 60000);
    return () => { stop = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk, resident]);

  const triggerEmergency = async (severity = "emergency") => {
    if (!kiosk) return;
    triggerSourceRef.current = "manual_kiosk";
    // A real user tap — prime audio+mic so the call can actually connect
    const ok = await primeMedia();
    if (!ok) return;
    setCallState("calling");
    try {
      const { data } = await axios.post(`${API}/alerts`, {
        kiosk_id: kiosk.kiosk_id,
        severity,
        message: severity === "emergency" ? "Emergency button pressed" : "Assistance requested",
        triggered_by: "kiosk_button",
      });
      activationGateRef.current.accept(data, true);
      setAlert(data);
      beginConversation(data);   // same TV/speaker duck as every other call start (2026-10-16)
    } catch {
      toast.error("Could not send the call. Please try again.");
      setCallState("idle");
    }
  };

  const cancelCall = async () => {
    await restoreAudioAfterCall("cancel_call");
    setAlert(null);
    setCallState("idle");
  };

  const sendDeviceCommand = async (action, value, kind, deviceId) => {
    if (!kiosk?.room) return;
    try {
      await sendRoomDeviceCommand(kiosk.room, action, value, kind, deviceId);
      toast.success(`${action} → ${value}`);
      // Refresh device state
      try {
        const { data: allDev } = await axios.get(`${API}/devices/public/by-room/${kiosk.room}`);
        setDevices(allDev || []);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reach the device");
    }
  };

  // IDLE state layout
  // Voice picker dialog — rendered in BOTH the idle and chat return blocks
  // so the user can change voices from any state.
  const voicePickerDialog = (
    <Dialog open={voicePickerOpen} onOpenChange={setVoicePickerOpen}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-caos-forest">Choose Aria's voice</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-caos-mute mb-4">
          Tap ▶ to preview. Tap the name to select. The voice is remembered per kiosk.
        </p>
        <div className="space-y-2">
          {VOICES.map((v) => {
            const selected = voiceId === v.id;
            return (
              <div
                key={v.id}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  selected ? "border-caos-forest bg-caos-forest/5" : "border-caos-line hover:border-caos-forest"
                }`}
                data-testid={`voice-row-${v.id}`}
              >
                <button
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/ai/tts`, {
                        text: "Hello. This is how I sound. I'll be right here with you.",
                        voice: v.id,
                        ...(voiceMode.transport === "cascade" ? { model: "aria_voice" } : {}),   // preview what that path speaks with
                      }, { timeout: 10000 });
                      const a = new Audio(`data:${data.mime || "audio/mp3"};base64,${data.audio_base64}`);
                      await a.play();
                    } catch { toast.error("Couldn't preview this voice"); }
                  }}
                  data-testid={`voice-preview-${v.id}`}
                  className="w-10 h-10 rounded-full bg-caos-forest text-white flex items-center justify-center hover:bg-caos-forest-hover flex-shrink-0"
                  aria-label={`Preview ${v.label}`}
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setVoiceId(v.id); voiceMode.setVoice(v.id); toast.success(`Voice set to ${v.label}`); }}
                  data-testid={`voice-select-${v.id}`}
                  className="flex-1 text-left"
                >
                  <div className="font-display font-medium text-caos-forest text-lg">
                    {v.label} {selected && <span className="ml-2 text-xs font-bold uppercase tracking-widest text-caos-forest/70">★ selected</span>}
                  </div>
                  <div className="text-sm text-caos-mute">{v.desc}</div>
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-caos-mute italic mt-4 border-t border-caos-line pt-3">
          Note: The available voices above are the supported kiosk voices for this deployment.
        </p>
      </DialogContent>
    </Dialog>
  );

  if (demoSetupError) return <RoomScreenPreview />;

  if (callState === "idle") {
    return (
      <div className={`min-h-screen bg-caos-ambient text-caos-ink p-8 md:p-12 flex flex-col ${a11yRootClass}`}>
        {isDemo && <DemoSiteBar highContrast={highContrast} onToggleContrast={() => setHighContrast((v) => !v)} />}
        {inSandboxedIframe() && (
          <button
            onClick={openInFullTab}
            data-testid="kiosk-iframe-warning"
            className="mb-4 rounded-2xl border-2 border-caos-amber bg-caos-amber/10 px-4 py-3 text-left flex items-center gap-3 hover:bg-caos-amber/20 transition-colors"
            title="Voice features require a full browser tab"
          >
            <AlertCircle className="w-5 h-5 text-caos-amber shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-caos-forest text-sm">Voice features need a full browser tab</p>
              <p className="text-caos-mute text-xs">The preview pane can't request microphone access. Tap here to open the kiosk in a real tab — then the browser will prompt for mic permission as usual.</p>
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-caos-forest shrink-0">Open →</span>
          </button>
        )}
        {/* Wraps on a phone-width screen so the high-contrast (HC) button is never pushed off the edge (2026-10-12). */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="font-display font-bold tracking-tighter text-caos-forest text-2xl">CAOS</span>
            <span className="font-display font-light text-caos-forest text-2xl">Care</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 kiosk-a11y-row">
            <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-white border border-caos-line" data-testid="kiosk-a11y-magnification">
              <button
                onClick={() => setMagnification(magnification - 20)}
                aria-label="Make text smaller"
                title="Make text smaller"
                className="p-2 rounded-full text-caos-forest hover:bg-caos-forest hover:text-white"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs font-bold text-caos-forest w-10 text-center">{magnification}%</span>
              <button
                onClick={() => setMagnification(magnification + 20)}
                aria-label="Make text bigger"
                title="Make text bigger"
                className="p-2 rounded-full text-caos-forest hover:bg-caos-forest hover:text-white"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setVoicePickerOpen(true)}
              data-testid="kiosk-a11y-voice"
              aria-label={`Voice: ${voiceId}. Tap to change voice.`}
              title="Change Aria's voice"
              className="px-4 py-2 rounded-full bg-caos-forest text-white hover:bg-caos-forest-hover flex items-center gap-2 text-sm font-bold uppercase tracking-wider shadow-sm"
            >
              <Volume2 className="w-4 h-4" />
              <span className="opacity-80">Voice:</span>
              <span>{voiceId}</span>
            </button>
            {voiceMode.available && (
              <button
                onClick={voiceMode.toggle}
                data-testid="kiosk-a11y-transport"
                aria-label={`Aria talks with ${voiceMode.transport === "cascade" ? "the low-cost voice" : "Realtime"}. Tap to switch.`}
                title="Switch how Aria talks: low-cost voice or Realtime"
                className="px-4 py-2 rounded-full bg-caos-forest text-white hover:bg-caos-forest-hover text-sm font-bold uppercase tracking-wider shadow-sm"
              >
                {voiceMode.transport === "cascade" ? "Low-cost" : "Realtime"}
              </button>
            )}
            <button
              onClick={() => setHighContrast((v) => !v)}
              data-testid="kiosk-a11y-contrast"
              aria-label={`High contrast mode ${highContrast ? "on" : "off"}. Tap to toggle.`}
              title="High contrast"
              aria-pressed={highContrast}
              className={`px-3 py-2 rounded-full border flex items-center gap-1 text-sm font-bold uppercase tracking-wider ${
                highContrast
                  ? "bg-caos-forest text-white border-caos-forest"
                  : "bg-white text-caos-forest border-caos-line hover:bg-caos-forest hover:text-white"
              }`}
            >
              <Contrast className="w-4 h-4" /> {highContrast ? "HC on" : "HC"}
            </button>
            {kiosk && (
              <div className="text-right text-caos-mute text-sm ml-2" data-testid="kiosk-label">
                <div className="font-bold">Room {kiosk.room}</div>
                <div>{kiosk.zone}</div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center max-w-5xl mx-auto w-full">
          {kiosk && <ProfileHeader resident={resident} kiosk={kiosk} />}

          <div className="text-center max-w-4xl">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-caos-mute">
              CARE · powered by CAOS
            </p>
            <h1 className="kiosk-hello mt-5 font-display text-5xl md:text-7xl lg:text-[110px] font-light tracking-tighter leading-[0.95] text-caos-forest">
              {resident ? `Hello, ${resident.preferred_name || resident.name.split(" ")[0]}.` : "Hello."}
            </h1>
            <p className="kiosk-prompt mt-6 text-2xl md:text-3xl text-caos-ink/80 leading-snug">
              If you need help, press the big red button.<br />
              Pressing it sends an alert to the staff screen.
            </p>

            <button
              onClick={() => triggerEmergency("emergency")}
              data-testid="kiosk-emergency-btn"
              aria-label="Emergency call button - press for help"
              className="caos-emergency-btn mt-14 w-full max-w-2xl rounded-[48px] py-16 font-display font-semibold text-5xl md:text-6xl tracking-tight flex items-center justify-center gap-6"
            >
              <Phone className="w-14 h-14 md:w-16 md:h-16" strokeWidth={2.5} />
              CALL FOR HELP
            </button>

            <div className="mt-10 flex flex-wrap gap-6 justify-center">
              <Button
                onClick={() => triggerEmergency("comfort")}
                data-testid="kiosk-chat-btn"
                variant="outline"
                className="caos-kiosk-btn border-2 border-caos-forest text-caos-forest hover:bg-caos-forest hover:text-white bg-white px-10 rounded-[32px]"
              >
                <Mic className="w-6 h-6 mr-3" /> I just want to talk
              </Button>
            </div>
            {!micReady && (
              <p className="mt-4 text-sm text-caos-mute italic" data-testid="kiosk-mic-prime-hint">
                Tap any button above — this screen will ask for microphone permission once, then the voice is hands-free.
              </p>
            )}
          </div>

          {/* Resident information hierarchy - requests/schedule/devices,
              same underlying state Aria's tools read (see 2026-08-27 report) */}
          <div className="w-full text-left mt-14 space-y-0">
            {isDemo && <DemoKioskPanel room={kiosk?.room} onStartTyped={startTypedConversation} />}
            <UpcomingPanel residentId={resident?.resident_id} room={kiosk?.room} />
            <RequestsPanel residentId={resident?.resident_id} room={kiosk?.room} />
            <TodayPanel />
            <RoomDevicePanel devices={devices} room={kiosk?.room} onCommand={sendDeviceCommand} />
          </div>
        </div>

        <div className="text-center text-caos-mute text-sm">
          Staff?{" "}
          <button onClick={() => nav("/login")} className="underline hover:text-caos-forest" data-testid="kiosk-staff-link">
            Staff sign in
          </button>
        </div>

        {/* Voice picker dialog — shared with chat state via voicePickerDialog */}
        {voicePickerDialog}
      </div>
    );
  }

  // CALLING / WAITING / CHATTING
  // Full-duplex Realtime (WebRTC) is the only resident conversation path.
  // Calling+waiting are skipped because the realtime peer connection
  // establishes in under a second.
  return (
    <>
      <RealtimeChatScreen
        resident={resident}
        kiosk={kiosk}
        voiceId={voiceId}
        transport={voiceMode.transport}
        a11yRootClass={a11yRootClass}
        triggerSource={triggerSourceRef.current}
        alertId={alert?.alert_id}
        activationId={alert?.activation_id}
        demo={isDemo}
        initialText={initialTypedText}
        onOpenVoicePicker={() => setVoicePickerOpen(true)}
        onEnd={(result) => {
          // The pendant retry gate only tracks resident-event cycles.
          if (triggerSourceRef.current !== "wake_word") activationGateRef.current.finish(result);
          setCallState("idle");
          setAlert(null);
          setInitialTypedText(null);
          restoreAudioAfterCall(result?.reason || "call_end");
        }}
      />
      {voicePickerDialog}

      {/* Tap-to-answer overlay when remote pendant fired but we have no user gesture yet */}
      {needsTap && (
        <button
          data-testid="kiosk-tap-to-answer"
          onClick={async () => {
            const ok = await primeMedia();
            if (!ok) return;
            setNeedsTap(false);
            const a = pendingAlert;
            setPendingAlert(null);
            if (a) await beginConversation(a);
          }}
          className="fixed inset-0 z-50 bg-caos-forest/95 text-white flex flex-col items-center justify-center gap-6 p-8 cursor-pointer"
        >
          <div className="animate-pulse">
            <Phone className="w-24 h-24" />
          </div>
          <p className="font-display text-5xl md:text-7xl font-light text-center">Incoming call</p>
          <p className="text-2xl md:text-3xl text-center max-w-2xl">
            {(pendingAlert?.resident_name || resident?.name || "A resident")} pressed their pendant.
          </p>
          <p className="text-xl uppercase tracking-[0.3em] text-caos-amber mt-4">TAP ANYWHERE TO ANSWER</p>
        </button>
      )}
      {alert?.severity === "emergency" && callState === "chatting" && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-caos-terracotta/10 border border-caos-terracotta rounded-2xl p-4 flex items-start gap-3 max-w-md">
          <AlertCircle className="w-6 h-6 text-caos-terracotta mt-0.5" />
          <p className="text-caos-terracotta-dark font-semibold">
            Emergency paged. Stay where you are. Staff have been notified.
          </p>
        </div>
      )}
    </>
  );
}
